import { Dispatch, SetStateAction, useEffect } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { BaseErrorResponse } from 'types/beneficiaries'
import { MappedErrorMessageState } from 'types/errors'
import { WalletType } from 'types/types'
import { floorWithPrecision } from 'utils/utils'
import { convertFromWalletAtom, convertToWalletAtom } from '../atoms/atoms'
import { ConvertDetailsForm, ConvertDetailsFormData } from '../schemas/convertDetailsSchema'

interface ConvertEffectsProps {
  convertFormValues: ConvertDetailsFormData
  exchangeRate: number
  formValues: ConvertDetailsForm
  sourceWallets: WalletType[]
  isErrorLimits: boolean
  ErrorLimits: Error
  setValue: UseFormSetValue<ConvertDetailsForm>
  setRequestError: Dispatch<SetStateAction<MappedErrorMessageState>>
  handleFetchQuoteEstimate: () => void
}

export const useConvertEffects = ({
  convertFormValues,
  exchangeRate,
  formValues,
  sourceWallets,
  isErrorLimits,
  ErrorLimits,
  setValue,
  setRequestError,
  handleFetchQuoteEstimate,
}: ConvertEffectsProps) => {
  const { t } = useTranslation()
  const [convertFromWallet, setConvertFromWallet] = useAtom(convertFromWalletAtom)
  const convertToWallet = useAtomValue(convertToWalletAtom)
  const [walletAction] = useWalletActionContext()

  useEffect(() => {
    if (convertFormValues?.changedField === 'toAmount') {
      setValue(
        'fromAmount',
        String(
          floorWithPrecision(
            Number(formValues?.toAmount) / exchangeRate,
            convertToWallet?.currency?.pricePrecision
          ) || ''
        )
      )
    }

    if (convertFormValues?.changedField === 'fromAmount') {
      setValue(
        'toAmount',
        String(
          floorWithPrecision(
            Number(formValues?.fromAmount) * exchangeRate,
            convertFromWallet?.currency?.pricePrecision
          ) || ''
        )
      )
    }
  }, [convertFormValues?.changedField, formValues?.fromAmount, formValues?.toAmount, exchangeRate])

  useEffect(() => {
    if (walletAction?.meta?.sourceWallet && sourceWallets && !convertFromWallet) {
      const sourceWallet = sourceWallets?.find(
        wallet => wallet?.lsid === walletAction?.meta?.sourceWallet?.lsid
      )

      if (!isEmpty(sourceWallet)) {
        setConvertFromWallet(sourceWallet)
      }
    }
  }, [walletAction, sourceWallets])

  useEffect(() => {
    if (convertToWallet) {
      setValue('toWallet', convertToWallet.lsid ?? '', {
        shouldValidate: true,
        shouldDirty: true,
      })
    } else {
      setValue('toWallet', '', {
        shouldValidate: false,
        shouldDirty: false,
      })
    }
  }, [convertToWallet])

  useEffect(() => {
    if (formValues?.toWallet) {
      handleFetchQuoteEstimate()
    }
  }, [formValues?.toWallet])

  useEffect(() => {
    if (isErrorLimits) {
      const error: BaseErrorResponse = ErrorLimits

      setRequestError({
        handledMessage: t('wallets.convert.oopsSomethingWentWrongFetchLimits'),
        errorCode: error?.status,
        show: true,
      })
    }
  }, [isErrorLimits, ErrorLimits])
}
